import { useEffect, useMemo, useState } from 'react';
import { Button, Badge } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useFilters, usePagination, useTable } from 'react-table';
import { toast } from 'react-toastify';
import { ErrorMessageHandler, getDates, toNepaliNumber } from '../../../../_helpers/_methods';
import { fetchWrapper } from '../../../../_helpers/fetchWrapper';
import DefaultColumnFilter from '../../../../Components/ReactTable/Filters/DefaultColumnFilter';
import ReactTableRender from '../../../../Components/ReactTable/ReactTableRender';
import Switch from 'react-switch';
import UserImage from '../../../../Components/UserImage';
import ToolTip from '../../../../Components/ToolTip';
import NepaliDate from 'nepali-date-converter';
import dayjs from 'dayjs';
import DownloadBtn from '../../../../Components/Buttons/DownloadBtn';
import { checkPermission } from '../../../../_helpers/roles/check-roles';
import { FormattedMessage, useIntl } from 'react-intl';

function Attendance() {
  const { id } = useParams();
  const intl = useIntl();
  const { user } = useSelector((state) => state.user);
  const [participants, setParticipants] = useState([]);
  const local = useSelector((state) => state.local.local);
  const event = useSelector((state) => state.event.event);
  const [eventDates, setEventsDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(null);
  const [pageNum] = useState(1);
  const completed = useSelector((state) => state.event.event.completed);

  const canUpdateAttendance = useMemo(() => {
    return checkPermission(user?.active_role, 'update attendance') && !completed;
  });

  useEffect(() => {
    const setEventDayHandler = () => {
      let startdate = event.start_date;
      let enddate = event.end_date;

      let eventDaysArray = getDates(startdate, enddate);
      let tempEventDaysArray = [];
      eventDaysArray.forEach((date) => {
        let thisDate = {
          date: dayjs(date).format('YYYY-MM-DD'),
          date_np: new NepaliDate(date).format('YYYY-MM-DD', 'np')
        };

        tempEventDaysArray.push(thisDate);
      });
      setEventsDates(tempEventDaysArray);
    };
    setEventDayHandler();
  }, [event]);

  useEffect(() => {
    const getParticipantsAttendance = async () => {
      try {
        setLoading(true);
        const participantsResponse = await fetchWrapper.get(
          `/event/${id}/participants/?selected=true`
        );
        const attendanceResponse = await fetchWrapper.get(`/event/${id}/attendance/`);
        let responseParticipants = participantsResponse.data.results;
        let attendanceData = attendanceResponse.data;
        setTotalCount(participantsResponse.data.count);
        let attendanceDataObj = attendanceData.reduce(
          (obj, item) =>
            Object.assign(obj, {
              [`${item.participant}_${item.date}`]: item.is_present
            }),
          {}
        );
        let tempParticipants = [];
        tempParticipants = responseParticipants.map((participant) => {
          let tempParticipant = { ...participant };
          tempParticipant.attendance = {};
          eventDates.forEach(({ date }) => {
            let isPresent =
              attendanceDataObj[`${tempParticipant.id}_${date}`] === undefined
                ? true
                : attendanceDataObj[`${tempParticipant.id}_${date}`];
            tempParticipant.attendance[date] = isPresent ? true : false;
          });
          return tempParticipant;
        });
        setParticipants(tempParticipants);
        setLoading(false);
      } catch (err) {
        setLoading(false);
        ErrorMessageHandler(err);
      }
    };
    if (eventDates.length) {
      getParticipantsAttendance();
    }
  }, [eventDates]);

  const defaultColumn = useMemo(
    () => ({
      // Let's set up our default Filter UI
      Filter: DefaultColumnFilter
    }),
    []
  );
  const handleSwitch = (participant, date) => {
    let tempParticipants = [...participants];
    let tempParticipantIndex = tempParticipants.findIndex((p) => p.id === participant.id);
    if (tempParticipantIndex !== -1) {
      tempParticipants[tempParticipantIndex].attendance[date] =
        !tempParticipants[tempParticipantIndex].attendance[date];
      setParticipants(tempParticipants);
    }
  };

  const columns = useMemo(() => {
    let staticColumns = [
      {
        Header: intl.formatMessage({ id: 'sn' }),
        accessor: 'serial_number',
        disableFilters: true,
        width: '5%'
      },
      {
        Header: intl.formatMessage({ id: 'attendance.fullname' }),
        accessor: 'fullname',
        Cell: ({ row: { original } }) => (
          <span className="d-flex align-items-center ">
            <div className="me-1 position-relative">
              <UserImage
                avatar={original.image}
                userName={original.fullname}
                userId={String(original.id)}
              />
              <span className="avatar-profile-iscomplete-badge">
                {original.warnings && (
                  <ToolTip warnings={original.warnings} isComplete={original.is_profile_updated} />
                )}
              </span>
            </div>
            <span className="d-block">
              {original.fullname} <br />
              {original.office_name ? `(${original.office_name})` : ''}
            </span>
            {original.suspended && <Badge bg="danger">Suspended</Badge>}
          </span>
        ),
        width: '30%'
      },
      {
        Header: intl.formatMessage({ id: 'attendance.phone_no' }),
        accessor: 'phone_no',
        width: '30%'
      }
    ];
    let dynamicColumns = [];
    eventDates.forEach((date) => {
      dynamicColumns.push({
        Header: new NepaliDate(new Date(date.date)).format(
          'YYYY/MM/DD',
          local === 'ne' ? 'np' : 'en'
        ),
        accessor: `${date.date}`,
        disableFilters: true,
        Cell: ({ row: { original } }) => {
          return (
            <span>
              <span>
                <Switch
                  onChange={() => handleSwitch(original, date.date)}
                  name="test"
                  checked={original[date.date]}
                  disabled={!canUpdateAttendance}
                />
              </span>
            </span>
          );
        }
      });
    });

    let Columns = [...staticColumns, ...dynamicColumns];
    return Columns;
  }, [eventDates, local, participants]);

  const data = useMemo(() => {
    if (!participants) return [];

    let count = 1;
    return participants.map((result) => {
      return {
        serial_number: local === 'ne' ? toNepaliNumber(count++) : count++,
        id: result.id,
        fullname: result.fullname,
        phone_no: result.phone,
        office_name: result.office_name,
        ...result.attendance
      };
    });
  }, [participants, local]);

  const {
    headerGroups,
    getTableProps,
    getTableBodyProps,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    visibleColumns,
    setPageSize,
    state: { pageIndex, pageSize }
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize: 50 },
      manualPagination: true,
      pageCount: pageNum,
      defaultCanFilter: false,
      defaultColumn
      // manualFilters: true
    },
    useFilters,
    usePagination
  );
  const pageCountArray = useMemo(
    () => Array.from({ length: Math.ceil(totalCount / pageSize) }, (v, i) => i + 1),
    [totalCount]
  );

  const handleSubmit = async () => {
    try {
      let attendanceData = [];
      participants.forEach((participant) => {
        let participantAttendance = {};
        participantAttendance.participant = participant.id;
        let attendances = [];
        Object.keys(participant.attendance).forEach((key) => {
          attendances.push({
            date: key,
            is_present: participant.attendance[key]
          });
        });
        participantAttendance.attendances = attendances;
        attendanceData.push(participantAttendance);
      });
      await fetchWrapper.post(`/event/${id}/attendance/`, attendanceData);
      toast.success('Attendance updated successfully');
    } catch (error) {
      ErrorMessageHandler(error);
    }
  };

  return (
    <div className="card">
      <div className="card-body">
        <div className="border-right">
          <div>
            <ReactTableRender
              getTableProps={getTableProps}
              headerGroups={headerGroups}
              getTableBodyProps={getTableBodyProps}
              page={page}
              prepareRow={prepareRow}
              totalCount={totalCount}
              gotoPage={gotoPage}
              previousPage={previousPage}
              pageCount={pageCount}
              pageCountArray={pageCountArray}
              pageNum={pageNum}
              showGoto={true}
              nextPage={nextPage}
              pageIndex={pageIndex}
              canNextPage={canNextPage}
              canPreviousPage={canPreviousPage}
              loading={loading}
              visibleColumns={visibleColumns}
              pagination={true}
              pageSize={pageSize}
              setPageSize={setPageSize}
              rightChildren={
                <div className="d-flex ps-4 justify-content-end" style={{ width: '50em' }}>
                  {checkPermission(user?.active_role, 'export files') && (
                    <>
                      <DownloadBtn
                        apiUrl={`/report/each-event/attendance/?event_id=${event.id}&type=excel`}
                        type="excel"
                      />
                      <DownloadBtn
                        apiUrl={`/report/each-event/attendance/?event_id=${event.id}&type=pdf`}
                        type="pdf"
                      />
                    </>
                  )}{' '}
                  {canUpdateAttendance && (
                    <div className="ps-3">
                      <Button onClick={handleSubmit}>
                        <FormattedMessage id="update_attendance" />
                      </Button>
                    </div>
                  )}
                </div>
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default Attendance;
